import { Directive, ElementRef, Input, OnChanges, Renderer2, SimpleChanges } from '@angular/core';
import { IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { FcpTreeItemMapper } from '../../domain/mappers/fcpTreeItem.mapper';
import { FcpTreeItemType } from '../../domain/models/fcp.model';
import { FcpTreeItem } from '../../domain/models/fcpTreeItem.model';
import { FcpFeatureState } from '../../domain/reducers/fcp.reducer';

@UntilDestroy()
@Directive({
    selector: 'app-fcp-tree-element',
})
export class FcpTreeElement implements OnChanges {
    FcpTreeItemType = FcpTreeItemType; // Make enum accessible in template
    @Input() item: FcpTreeItem;
    @Input() showAdjustments: boolean = false;
    @Input() outputFinStatements: IFinStatements[];
    @Input() filter: string = '';
    active$: Observable<boolean>;
    protected protectClosingAndOpeningValues = false;

    constructor(
        protected readonly store: Store<FcpFeatureState>,
        private el: ElementRef,
        private renderer: Renderer2,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['item']) {
            this.calculateTotalValues();
        }

        if (changes['filter']) {
            this.updateFilterClass();
        }
    }

    protected filterMethod(item: FcpTreeItem): boolean {
        try {
            return item.name.toLowerCase().includes(this.filter.toLowerCase());
        } catch (error) {
            //Error can be ignored, because in the case of an error the item will be filtered out
            return false;
        }
    }

    private updateFilterClass(): void {
        this.renderer.removeClass(this.el.nativeElement, 'filteredOut');
        if (this.filter !== '' && this.item && !this.filterMethod(this.item)) {
            this.renderer.addClass(this.el.nativeElement, 'filteredOut');
        } else {
            this.renderer.removeClass(this.el.nativeElement, 'filteredOut');
            // here it should check if any parent contains the class filteredOut and remove it in this case
            this.removeFilteredOutFromParents(this.el.nativeElement);
        }
    }

    // Recursive method to remove 'filteredOut' class from parent elements and their siblings
    private removeFilteredOutFromParents(element: any): void {
        const parent = element.parentElement;

        if (parent) {
            // Remove 'filteredOut' class from the parent element if present
            if (parent.classList.contains('filteredOut')) {
                this.renderer.removeClass(parent, 'filteredOut');
            }

            // Check if the parent element's parent exists before accessing children
            const grandParent = parent.parentElement;
            if (grandParent) {
                // Iterate through all child nodes of the grandparent to remove 'filteredOut' class from siblings
                Array.from(grandParent.children).forEach((sibling: Element) => {
                    if (sibling !== parent && sibling.classList.contains('filteredOut')) {
                        this.renderer.removeClass(sibling, 'filteredOut');
                    }
                });
            }

            // Continue checking up the DOM tree
            this.removeFilteredOutFromParents(parent);
        }
    }

    private calculateTotalValues(): void {
        if (this.item && this.item.children) {
            this.item.adjustedValue = this.item.children.reduce((sum, child) => sum + (child.adjustedValue || 0), 0);
            if (!this.protectClosingAndOpeningValues) {
                this.item.closingValue = this.item.children.reduce((sum, child) => sum + (child.closingValue || 0), 0);
                this.item.openingValue = this.item.children.reduce((sum, child) => sum + (child.openingValue || 0), 0);
            }
        }
    }

    protected calculateAdjustedValues(): number {
        const outPutSortedItem = FcpTreeItemMapper.findSortedItemByUid(this.outputFinStatements, this.item.uid);
        if (outPutSortedItem == null) {
            return 0;
        } else {
            return FcpTreeItemMapper.sumAccounts(outPutSortedItem).totalClosingBalance;
        }
    }

    get totalAdjustedValue(): number {
        return this.calculateAdjustedValues();
    }

    get totalClosingValue(): number {
        return this.item.closingValue;
    }

    get totalOpeningValue(): number {
        return this.item.openingValue;
    }

    hasTestItems(item: FcpTreeItem): boolean {
        if (!item.children || item.children.length === 0) {
            return false;
        }

        for (const child of item.children) {
            if (child.type === FcpTreeItemType.TestItem) {
                return true;
            } else if (child.type === FcpTreeItemType.Bundle) {
                if (this.hasTestItems(child)) {
                    return true;
                }
            } else if (child.type === FcpTreeItemType.TestGroup) {
                if (this.hasTestItems(child)) {
                    return true;
                }
            }
        }

        return false;
    }
}
